import { DataTable } from "@/components/DataTable/dataTable.component";
import RepeatOrder from "@/components/OrderTrackingTable/repeatOrder.component";
import SelectBranch from "@/components/OrderTrackingTable/SelectBranch/selectBranch.component";
import ViewPDF from "@/components/ViewPDF/viewPDF.component";
import { fetchSingleOrder } from "@/models/socket/orders.model";
import { Button } from "@/shadcn/components/ui/button";
import { Card, CardContent } from "@/shadcn/components/ui/card";
import { ConvertedOrderItem, FullSingleOrder, OrderBranches } from "@/types";
import { convertOrderItem } from "@/utils/dataConversion";
import {
  exportSingleOrderToCSVs,
  exportSingleOrderToExcel,
} from "@/utils/Exports/exportSingleOrder.util";
import { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";

type SingleOrderItemsTableProps = {
  orderID: string;
  closeTable: () => void;
};

const SingleOrderItemsTable: React.FC<SingleOrderItemsTableProps> = ({
  orderID,
  closeTable,
}) => {
  const [singleOrder, setSingleOrder] = useState<FullSingleOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [convertedItems, setConvertedItems] = useState<ConvertedOrderItem[]>(
    []
  );
  const [viewingPDF, setViewingPDF] = useState<boolean>(false);
  const [branches, setBranches] = useState<OrderBranches[]>([]);

  useEffect(() => {
    async function fetchSingleData() {
      if (!orderID) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetchSingleOrder(orderID);
        if (response.rStatus === "error") {
          throw new Error(response.rData);
        }
        setSingleOrder(response.rData.order);
        const covetedItemsList = await convertOrderItem(response.rData.order);
        setConvertedItems(covetedItemsList);
        setBranches(response.rData.branches || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchSingleData();
  }, [orderID]);

  const columns: ColumnDef<ConvertedOrderItem>[] = [
    // {
    //   header: "View",
    //   accessorKey: "view",
    //   cell: ({ row }) => {
    //     // Add view action at the end for user convenience
    //     return (
    //       <div className="flex items-center justify-center">
    //         <DialogViewImage imgScr={row.original.item_file_meta || ""} />
    //       </div>
    //     );
    //   },
    //   enableSorting: false,
    //   enableHiding: false,
    // },
    {
      header: "Part No",
      accessorKey: "part_no",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Length (mm)",
      accessorKey: "length",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Width (mm)",
      accessorKey: "width",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Material Grade",
      accessorKey: "material_grade",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Thickness (mm)",
      accessorKey: "thickness",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Weight (kg)",
      accessorKey: "weight",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Quantity",
      accessorKey: "qty",
      enableSorting: true,
      enableHiding: false,
      size: 70,
      cell: ({ cell }) => {
        const value = cell.getValue();
        return <span>{value !== null ? String(value) : "N/A"}</span>;
      },
    },
    {
      header: "Cut",
      accessorKey: "cut",
      enableSorting: true,
      enableHiding: false,
      size: 60,
      cell: ({ cell }) => <span>{cell.getValue() === "Y" ? "Yes" : "No"}</span>,
    },
    {
      header: "Bend",
      accessorKey: "bend",
      enableSorting: true,
      enableHiding: false,
      size: 60,
      cell: ({ cell }) => <span>{cell.getValue() === "Y" ? "Yes" : "No"}</span>,
    },
    {
      header: "Shot",
      accessorKey: "shot",
      enableSorting: true,
      enableHiding: false,
      size: 60,
      cell: ({ cell }) => <span>{cell.getValue() === "Y" ? "Yes" : "No"}</span>,
    },
    {
      header: "DN Issued",
      accessorKey: "delivery_note_issued",
      enableSorting: true,
      enableHiding: false,
      size: 80,
      // This field may not exist on OrderItem; included for requested heading
      cell: ({ cell }) => (
        <span>
          {cell.getValue() === "Y"
            ? "Yes"
            : cell.getValue() === "N"
              ? "No"
              : "N/A"}
        </span>
      ),
    },
    {
      header: "DN POD",
      accessorKey: "delivery_note_proof_of_delivery",
      enableSorting: true,
      enableHiding: false,
      size: 80,
      // This field may not exist on OrderItem; included for requested heading
      cell: ({ cell }) => (
        <span>
          {cell.getValue() === "Y"
            ? "Yes"
            : cell.getValue() === "N"
              ? "No"
              : "N/A"}
        </span>
      ),
    },
    {
      header: "Drawing Number",
      accessorKey: "item_file_drawing_no",
      enableSorting: true,
      enableHiding: false,
      size: 350,
      // This field may not exist on OrderItem; included for requested heading
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "NC Number",
      accessorKey: "nc_number",
      enableSorting: true,
      enableHiding: false,
      size: 100,
      // This field may not exist on OrderItem; included for requested heading
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: "Query",
      accessorKey: "assigned_to",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      // This field may not exist on OrderItem; included for requested heading
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
  ];

  function exportToExcel() {
    if (!singleOrder) {
      console.error("No order data available for export");
      return;
    }
    exportSingleOrderToExcel(singleOrder);
  }

  function exportToCSV() {
    if (!singleOrder) {
      console.error("No order data available for export");
      return;
    }
    exportSingleOrderToCSVs(singleOrder);
  }

  function handleChangeBranch(branchID: string) {
    // Handle branch change if needed
    console.log("Selected branch ID:", branchID);
    setSingleOrder((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        branch_id: branchID, // Update branch ID in the order
      };
    });
  }

  return (
    <Card className="relative bg-black/10">
      <X
        className="absolute top-2 right-2 cursor-pointer text-white"
        onClick={closeTable}
      />
      {loading ? (
        <div className="py-4">
          <div className="flex items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
          </div>
          <div className="flex items-center justify-center">
            <h2 className="mt-2 text-lg text-white">
              Loading Order Details...
            </h2>
          </div>
        </div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : !singleOrder ? (
        <div className="flex items-center justify-center">
          <p className="hidden text-red-500">
            {/* Should not happen, handled by error */}
            No order details found for ID: {orderID}
          </p>
        </div>
      ) : (
        <>
          {viewingPDF && singleOrder.file_meta && (
            <ViewPDF
              fileBase64={singleOrder.file_meta}
              closeViewer={() => setViewingPDF(false)}
            />
          )}
          {/* <CardHeader className="m-0 p-0">
            <CardTitle className="text-center text-lg font-bold">
              <div>Order Details for order {orderID}</div>
            </CardTitle>
          </CardHeader> */}
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                className="mb-4"
                // variant="outline"
                onClick={exportToExcel}
              >
                Export to Excel
              </Button>
              <Button
                className="mb-4"
                // variant="outline"
                onClick={exportToCSV}
              >
                Export to CSV
              </Button>
              {/* {singleOrder.file_meta && (
                <Button
                  className="mb-4"
                  // variant="outline"
                  onClick={() => setViewingPDF(true)}
                >
                  View PDF
                </Button>
              )} */}
              <RepeatOrder orderID={orderID} />
              <SelectBranch
                orderID={orderID}
                currentBranchID={singleOrder.branch_id}
                setCurrentBranchID={handleChangeBranch}
                branches={branches}
              />
            </div>
            <DataTable
              columns={columns}
              data={convertedItems}
              includeSelectRow={false}
              disableColumnSelection={true}
            />
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default SingleOrderItemsTable;
